/**
 * WhatsApp Link Generator for Follow-ups
 * Generates WhatsApp links with pre-written follow-up messages.
 * Phone formatting / deep-link builders live in shared outboundMessaging.
 */

import {
  buildWhatsAppUrl,
  formatPhoneForWhatsApp,
  openWhatsAppUrl,
} from "../../../shared/utils/outboundMessaging.js";

export { formatPhoneForWhatsApp };

/**
 * Generate WhatsApp message for quote follow-up
 * @param {Object} followUp - Follow-up data
 * @param {Object} quote - Quote data (optional, for context)
 * @returns {string} Formatted WhatsApp message
 */
export const generateQuoteFollowUpMessage = (followUp, quote = null) => {
  const customerName = followUp.entityContext?.name || quote?.customerName || "there";
  const referenceNumber = followUp.entityContext?.referenceNumber || quote?.id || "";
  const quoteAmount = followUp.entityContext?.amount || quote?.totalCost || quote?.estimatedCost;
  const from = followUp.entityContext?.from || quote?.from || "";
  const to = followUp.entityContext?.to || quote?.to || "";
  const date = followUp.entityContext?.date || quote?.date || "";
  
  let message = `Good day ${customerName}! 👋\n\n`;
  message += `We wanted to follow up on your quote.\n\n`;
  
  if (referenceNumber) {
    message += `*Quote Reference:* ${referenceNumber}\n`;
  }
  
  if (quoteAmount) {
    const formattedAmount = typeof quoteAmount === 'number' 
      ? quoteAmount.toLocaleString("en-ZA")
      : quoteAmount;
    message += `*Quote Amount:* R${formattedAmount}\n`;
  }
  
  if (from && to) {
    message += `*Route:* ${from} → ${to}\n`;
  }
  
  if (date && date !== "Not specified") {
    message += `*Move Date:* ${date}\n`;
  }
  
  message += `\nIs there anything we can help you with? `;
  message += `Have you had a chance to review the quote? `;
  message += `We'd love to hear your thoughts or answer any questions you might have.\n\n`;
  message += `If you're ready to proceed, just let us know and we'll take care of the rest!\n\n`;
  message += `Best regards,\nBubakii Logistics Team`;

  return message;
};

/**
 * Generate WhatsApp link for follow-up
 * @param {Object} followUp - Follow-up data
 * @param {Object} quote - Quote data (optional, for additional context)
 * @returns {string|null} WhatsApp link URL or null if phone number is missing
 */
export const generateFollowUpWhatsAppLink = (followUp, quote = null) => {
  if (!followUp) {
    throw new Error("Follow-up data is required");
  }

  // Get phone from entityContext or quote
  const phone = followUp.entityContext?.phone || quote?.customerPhone;
  
  if (!phone) {
    return null;
  }

  let message;
  
  // Generate message based on entity type
  if (followUp.entityType === "quote") {
    message = generateQuoteFollowUpMessage(followUp, quote);
  } else {
    // Fallback to template message if available, or description
    message = followUp.templateMessage || followUp.description || "Follow-up message";
  }
  
  return buildWhatsAppUrl(phone, message);
};

/**
 * Open WhatsApp link for follow-up in new tab
 * @param {Object} followUp - Follow-up data
 * @param {Object} quote - Quote data (optional)
 * @returns {void}
 */
export const openFollowUpWhatsAppLink = (followUp, quote = null) => {
  const link = generateFollowUpWhatsAppLink(followUp, quote);
  if (link) {
    openWhatsAppUrl(link);
  } else {
    console.warn("Cannot generate WhatsApp link: phone number missing");
  }
};
